import dayjs from 'dayjs';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { theme } from '@frontend/theme';
import { Button, Heading, Text } from '@frontend/design-system';
import { RecentCallRecord } from '../../all-calls/types';
import {
  TimelineSection,
  IssuesDetectedSection,
  MetricSection,
  RecommendationsSection,
} from './call-quality-components';
import { PanelHeader } from './common';
import { SCREENS } from './const';
import { CallDetailsContext, MetricData, ScreenBase } from './types';

const CallLabel = ({ record }: { record: RecentCallRecord }) => {
  const { t } = useTranslation('calls');

  const contact = record.callerName || record.callerNumber;
  const icon =
    record.direction === 'outbound' ? (
      <Icon color='light' name='phone-outgoing-small' />
    ) : (
      <Icon color='light' name='phone-incoming-small' />
    );
  const label =
    record.direction === 'outbound'
      ? t('Call to {{name}}', { name: contact })
      : t('Call from {{name}}', { name: contact });

  return (
    <div
      style={{
        gridArea: 'label',
        display: 'grid',
        alignItems: 'center',
        gap: theme.spacing(1),
        gridTemplateColumns: 'repeat(4, minmax(0px,max-content))',
      }}
    >
      {icon}
      <Text as='span' size='medium'>
        {label}
      </Text>
      <Text as='span' size='medium'>
        •
      </Text>
      <Text as='span' size='medium'>
        {dayjs(record.answeredAt).format('ddd MMM D, YYYY, h:mm A')}
      </Text>
    </div>
  );
};

export const CallQualityScreen = ({ setScreen }: ScreenBase) => {
  const { t } = useTranslation('calls');
  const { context } = useSlidePanelShallowStore<CallDetailsContext>('setShow', 'context', 'show');
  const row = context?.rows.find((row) => row.id === context.currentRowId);

  const data: MetricData[] = [
    {
      type: 'mosMin',
      values: [{ sentiment: 'warn', label: '3.5/5' }],
    },
    {
      type: 'packetLossMax',
      values: [
        { sentiment: 'negative', direction: 'inbound', label: '4%' },
        { sentiment: 'negative', direction: 'outbound', label: '5%' },
      ],
    },
    {
      type: 'jitterMax',
      values: [
        { sentiment: 'warn', direction: 'inbound', label: '21 ms' },
        { sentiment: 'warn', direction: 'outbound', label: '16 ms' },
      ],
    },
  ];

  return (
    <div>
      <PanelHeader setScreen={setScreen}>
        <div
          style={{
            display: 'grid',
            alignItems: 'center',
            columnGap: theme.spacing(1),
            gridTemplateColumns: 'auto 1fr',
            gridTemplateAreas: '"back title" ". label"',
          }}
        >
          <Button
            iconName='caret-left'
            variant='secondary'
            css={{ gridArea: 'back' }}
            onClick={() => {
              setScreen(SCREENS.CALL_DETAILS);
            }}
          />

          <Heading level={2} css={{ margin: 0, gridArea: 'title' }}>
            {t('Network Performance')}
          </Heading>
          {row && <CallLabel record={row} />}
        </div>
      </PanelHeader>
      <div
        style={{
          display: 'grid',
          gridTemplateRows: 'repeat(auto-fit, auto)',
          padding: theme.spacing(3),
          gap: theme.spacing(2),
        }}
      >
        <IssuesDetectedSection variant='screen' />
        <div css={{ borderTop: `1px solid ${theme.colors.neutral20}` }} />
        <RecommendationsSection />
        <div css={{ borderTop: `1px solid ${theme.colors.neutral20}` }} />
        <MetricSection data={data} subtitle={t('These values highlight the worst call quality detected.')} />
        <div css={{ borderTop: `1px solid ${theme.colors.neutral20}` }} />
        <TimelineSection setScreen={setScreen} />
        {/* TODO: still need a feedback component here */}
      </div>
    </div>
  );
};
